import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColumnMenu } from "@mui/x-data-grid";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";

import Modal from "Components/Common/Modal";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";

import removeExtension from "utils/removeExtension";
import getFileExtension from "utils/getFileExtension";
import { generateFormData } from "utils";

import {
  usePostSendMailMutation,
  useUtilityConsumerContactInfoQuery,
} from "apis/UtilityConsumer/utilityConsumer";

import pdfImg from "assets/images/pdf.png";

import { StyledGrid, DocumentName } from "./style";
import { getOptionValue } from "common/enums";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const columns = [
  {
    headerName: "Customer Number",
    field: "consumerNumber",
    width: 150,
  },
  {
    headerName: "Customer Name",
    field: "consumerName",
    width: 350,
  },
  {
    headerName: "Email",
    field: "email",
    width: 350,
  },
  {
    headerName: "Alternate Email",
    field: "alternateEmail",
    renderCell: ({ row: { alternateEmail } }) => alternateEmail || "--",
    width: 350,
  },
  {
    headerName: "Mobile Number",
    field: "mobileNumber",
    width: 150,
  },
  {
    headerName: "Status",
    field: "status",
    width: 150,
  },
  {
    headerName: "Tariff Name",
    field: "tariff",
    width: 150,
  },
  {
    headerName: "Source Of Power",
    field: "sourceOfPower",
    width: 220,
  },
];

const initialValues = {
  subject: "",
  body: "",
  attachments: [],
  recipientEmails: [],
};

const SendMailModal = ({ handleClose, t, utility }) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [isSendBtnClicked, setIsSendBtnClicked] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);

  const {
    data: { data: utilityConsumerData = [] } = {},
    isLoading: isConsumerDataLoading,
  } = useUtilityConsumerContactInfoQuery({
    isForPowerConsumers: 
      getOptionValue("utilityOptions", "Power") === utility || !utility
    ,
    utility,
  });

  const [sendMail, { isLoading: isMailSending }] = usePostSendMailMutation();

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      subject: Yup.string().nullable().required("Subject is required"),
      body: Yup.string().nullable().required("Description is required"),
    }),
    onSubmit: async ({ subject, body, attachments, recipientEmails }) => {
      try {
        const formData = new FormData();

        generateFormData(formData, {
          subject,
          body,
          recipientEmails,
        });

        if (attachments.length) {
          attachments.forEach(({ document }) => {
            formData.append("attachments", document);
          });
        }

        await sendMail(formData);
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: { attachments, recipientEmails },
    handleSubmit,
    setFieldValue,
  } = formik;

  const fileChangeHandler = (event) => {
    try {
      const uploadedDocuments = Array.from(event.target.files);

      const isValidFileUploaded = uploadedDocuments.every(({ name }) =>
        validFileFormats.includes(getFileExtension(name.toLowerCase()))
      );

      if (!isValidFileUploaded) {
        toast.error("Invalid file format, Please upload pdf or images");
      } else {
        setFieldValue("attachments", [
          ...attachments,
          ...uploadedDocuments.map((document) => ({
            id: uniqueId(),
            document,
          })),
        ]);
        event.target.value = "";
      }
    } catch (err) {}
  };

  const fileClickHandler = (document) => {
    const fileExtension = getFileExtension(document.name ?? "");
    setDocumentToPreview({
      documentTypeName: document.name,
      url: URL.createObjectURL(document),
      documentType: fileExtension,
      documentName: removeExtension(document.name ?? ""),
    });
  };

  if (isConsumerDataLoading) {
    return <Backdrop open={isConsumerDataLoading} />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      {isSendBtnClicked && (
        <Modal
          isOpen
          title="Mail Information"
          maxWidth="md"
          handleClose={() => setIsSendBtnClicked(false)}
        >
          <div className="mt-3">
            <Grid item xs={12}>
              <InputFieldText
                label={t("Subject") + "*"}
                placeholder="Enter Subject"
                name="subject"
                formik={formik}
              />
            </Grid>

            <Grid item xs={12}>
              <InputFieldText
                label={t("Description") + "*"}
                placeholder="Enter Description"
                name="body"
                formik={formik}
                multiline
                rows={6}
                InputProps={{
                  endAdornment: (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                      }}
                    >
                      <IconButton color="primary" component="label">
                        <AttachmentIcon />
                        <input
                          style={{
                            display: "none",
                          }}
                          accept="image/*, .pdf, .PDF"
                          type="file"
                          onChange={fileChangeHandler}
                          multiple="multiple"
                        />
                      </IconButton>
                    </div>
                  ),
                }}
              />
            </Grid>

            {!!attachments.length && (
              <>
                <Grid item xs={12}>
                  <Typography className="mt-2 fw-semibold">
                    {t("Documents")} :
                  </Typography>
                </Grid>
                <Grid item xs={12} className="mb-3 ms-1 mt-1">
                  <div className="d-flex gap-3 flex-wrap mt-3">
                    {attachments.map(({ document, id: selectedId }, index) => (
                      <div className="image-container" key={index}>
                        <Box
                          component="img"
                          className="image d-block m-auto"
                          alt="document image"
                          src={
                            getFileExtension(document.name) === "pdf"
                              ? pdfImg
                              : URL.createObjectURL(document)
                          }
                          onClick={() => fileClickHandler(document)}
                        />
                        <i
                          className="ri-close-circle-line fs-3 close-btn"
                          onClick={() =>
                            setFieldValue(
                              "attachments",
                              attachments.filter(({ id }) => id !== selectedId)
                            )
                          }
                        />
                        <Tooltip title={document.name}>
                          <DocumentName fontSize={13} variant="body2">
                            {document.name}
                          </DocumentName>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </Grid>
              </>
            )}

            <Grid item xs={12} className="text-end mt-3">
              <Button
                onClick={handleSubmit}
                color="primary"
                className="mb-1 me-1"
                variant="contained"
                disabled={isMailSending}
              >
                {t("Save")}
                {isMailSending && <CircularProgress size={20} />}
              </Button>
            </Grid>
          </div>
        </Modal>
      )}
      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Send Mail")}
        maxWidth="lg"
      >
        <StyledGrid
          checkboxSelection
          autoHeight
          disableRowSelectionOnClick
          rows={utilityConsumerData}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          rowCount={utilityConsumerData?.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(ids) => {
            // eslint-disable-next-line no-undef
            const selectedIDs = new Set(ids);
            const selectedRows = utilityConsumerData
              .filter(({ id }) => Array.from(selectedIDs).includes(id))
              .flatMap(({ email, alternateEmail }) => [
                ...(email ? [email] : []),
                ...(alternateEmail ? [alternateEmail] : []),
              ]);
            // eslint-disable-next-line no-undef
            const selectedMails = new Set(selectedRows);
            setFieldValue("recipientEmails", Array.from(selectedMails));
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("No records found")}.
              </Stack>
            ),
          }}
          rowSelection
          keepNonExistentRowsSelected
        />
        <div className="text-end">
          <Button
            variant="contained"
            className="mt-2"
            color="success"
            disabled={!recipientEmails.length}
            onClick={() => {
              setIsSendBtnClicked(true);
            }}
          >
            {t("Send")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

SendMailModal.propTypes = {
  handleClose: PropTypes.func,
  utility: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(SendMailModal);
